import { useState, useEffect } from "react";
import { X, CaretLeft, CaretRight, Lightbulb, Brain, CursorClick, Check } from "@phosphor-icons/react";
import { BRAND } from "../lib/brand";

export const WELCOME_TOUR_KEY = "gcr_welcome_seen";

const STEPS = [
  {
    icon: Lightbulb,
    title: "Una señal, explicada",
    text: "Cada jugada del día viene con su análisis en palabras simples: qué significa la apuesta, cuándo ganas y dónde ponerla en tu casa de apuestas.",
  },
  {
    icon: Brain,
    title: "El IQ del Día",
    text: "Nuestra jugada de mayor confianza, calculada con 50,000 simulaciones. Es la primera que debes mirar cada día.",
  },
  {
    icon: CursorClick,
    title: "Así la pones",
    text: "Abre la jugada, lee la guía «Dónde ponerla» y colócala en tu casa de apuestas (Bet365, etc.). Empieza con montos pequeños.",
  },
];

export default function WelcomeTour({ open, onClose }) {
  const [step, setStep] = useState(0);

  // Bloquear scroll del fondo mientras el tour está abierto
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open ]);

  if (!open) return null;

  const finish = () => {
    try { localStorage.setItem(WELCOME_TOUR_KEY, "1"); } catch { /* noop */ }
    setStep(0);
    onClose?.();
  };

  const isLast = step === STEPS.length - 1;
  const { icon: Icon, title, text } = STEPS[step];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998]"
        style={{
          backdropFilter: "blur(6px) brightness(0.45)",
          WebkitBackdropFilter: "blur(6px) brightness(0.45)",
          background: "rgba(0,0,0,0.55)",
        }}
        onClick={finish}
        data-testid="welcome-tour-backdrop"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        data-testid="welcome-tour-overlay"
      >
        <div
          className="relative w-full rounded-2xl overflow-hidden"
          style={{
            maxWidth: 380,
            background: "linear-gradient(160deg, #16161E 0%, #0E0E14 100%)",
            border: "1px solid rgba(212,175,55,0.25)",
            boxShadow: "0 24px 60px -8px rgba(0,0,0,0.7), 0 0 40px -10px rgba(212,175,55,0.15)",
            animation: "ageIn 0.3s cubic-bezier(0.34,1.4,0.64,1) forwards",
          }}
          data-testid="welcome-tour-modal"
        >
          {/* Gold hairline top */}
          <div className="h-[1.5px]" style={{ background: "linear-gradient(90deg, transparent 0%, #D4AF37 50%, transparent 100%)" }} />

          {/* Cerrar */}
          <button
            onClick={finish}
            aria-label="Cerrar tour"
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            style={{ color: "#71717A", background: "rgba(255,255,255,0.04)" }}
            onMouseEnter={e => e.currentTarget.style.color = "#fff"}
            onMouseLeave={e => e.currentTarget.style.color = "#71717A"}
            data-testid="welcome-tour-close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="px-7 pt-8 pb-7 flex flex-col items-center text-center">
            {/* Marca */}
            <div className="text-[9px] tracking-[0.3em] uppercase font-black mb-4" style={{ color: "#D4AF37" }}>
              Bienvenido a {BRAND.name}
            </div>

            {/* Icono del paso */}
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
              style={{
                background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.05))",
                border: "1px solid rgba(212,175,55,0.35)",
                boxShadow: "0 8px 24px -6px rgba(212,175,55,0.3)",
              }}
            >
              <Icon weight="duotone" className="w-8 h-8" style={{ color: "#D4AF37" }} />
            </div>

            {/* Contenido del paso */}
            <h2 className="font-sport font-black text-white text-2xl mb-3" style={{ letterSpacing: "-0.02em" }}>
              {title}
            </h2>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "#A1A1AA", minHeight: 66 }}>
              {text}
            </p>

            {/* Dots de progreso */}
            <div className="flex items-center gap-2 mb-6" data-testid="welcome-tour-dots">
              {STEPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  aria-label={`Ir al paso ${i + 1}`}
                  className="rounded-full transition-all"
                  style={{
                    width: i === step ? 24 : 8,
                    height: 8,
                    background: i === step ? "#D4AF37" : "rgba(255,255,255,0.15)",
                  }}
                />
              ))}
            </div>

            {/* Botones */}
            <div className="w-full flex items-center gap-3">
              {step > 0 ? (
                <button
                  onClick={() => setStep(s => s - 1)}
                  className="flex-1 py-3.5 rounded-xl text-xs font-black uppercase tracking-widest transition-colors"
                  style={{ color: "#A1A1AA", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
                  data-testid="welcome-tour-back"
                >
                  <span className="inline-flex items-center gap-1">
                    <CaretLeft weight="bold" className="w-3.5 h-3.5" /> Atrás
                  </span>
                </button>
              ) : (
                <button
                  onClick={finish}
                  className="flex-1 py-3.5 rounded-xl text-xs font-black uppercase tracking-widest transition-colors"
                  style={{ color: "#71717A", background: "transparent" }}
                  data-testid="welcome-tour-skip"
                >
                  Omitir
                </button>
              )}
              <button
                onClick={() => (isLast ? finish() : setStep(s => s + 1))}
                className="flex-[2] py-3.5 rounded-xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-1.5"
                style={{ background: "linear-gradient(135deg, #E8C766 0%, #D4AF37 100%)", color: "#050508", boxShadow: "0 8px 24px -6px rgba(212,175,55,0.5)" }}
                data-testid={isLast ? "welcome-tour-finish" : "welcome-tour-next"}
              >
                {isLast ? (
                  <>Empezar <Check weight="bold" className="w-4 h-4" /></>
                ) : (
                  <>Siguiente <CaretRight weight="bold" className="w-3.5 h-3.5" /></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
